import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FlatList, View } from 'react-native';
import Amount from 'design-system/Amount';
import { rem } from 'design-system/values';
import normalize from 'common/Normalize';
import { formatPeriod } from 'utils/formatting';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { triggerSelectorPress } from 'utils/hapticFeedback';
import Text from 'design-system/Text';
import CategoriesHistogramBar, { OUTER_WIDTH as BAR_OUTER_WIDTH, } from './CategoriesHistogramBar';
import { DashedLine } from './DashedLine';
const CARD_HEIGHT = normalize(154);
const BASELINE_HEIGHT = rem(1);
const getItemLayout = (_, index) => ({
    length: BAR_OUTER_WIDTH,
    offset: BAR_OUTER_WIDTH * index,
    index,
});
const keyExtractor = (item, index) => `${item.label}-${index}`;
const noop = () => { };
const CategoriesHistogram = ({ lineGraph, selectedIndex, categoryMerchantSelected, color, onMonthSelected, currency, }) => {
    const styles = useStyles(styleSet);
    const [minValue, maxValue, sum] = useMemo(() => lineGraph.reduce(([minValue, maxValue, sum], { value }) => [
        value < minValue ? value : minValue,
        value > maxValue ? value : maxValue,
        sum + value,
    ], [0, 0, 0]), [lineGraph]);
    const barContainerHeight = useMemo(() => {
        let H = CARD_HEIGHT - rem(16) - rem(18);
        if (lineGraph.length > 0) {
            if (formatPeriod(lineGraph[0], true).length > 10) {
                H -= rem(18);
            }
        }
        return H;
    }, [lineGraph]);
    const drawBaseline = minValue < 0 && maxValue > 0;
    const baselineHeight = drawBaseline ? BASELINE_HEIGHT : 0;
    const [scale, baselineTop] = useMemo(() => {
        if (maxValue <= minValue) {
            return [0, barContainerHeight];
        }
        let scale = (barContainerHeight - baselineHeight) / (maxValue - minValue);
        let baselineTop = maxValue * scale;
        if (sum < 0) {
            // spending more than income. the bars will be reverted, i.e., spending above the baseline.
            scale = -scale;
            baselineTop = minValue * scale;
        }
        // reduce scale, but after calculating baseline positions.
        // it gives spring animation room at the margin.
        scale *= 0.8;
        return [scale, baselineTop];
    }, [barContainerHeight, baselineHeight, maxValue, minValue, sum]);
    const barItems = useMemo(() => lineGraph.map((item) => ({
        label: formatPeriod(item, true),
        signedHeight: item.value * scale,
    })), [lineGraph, scale]);
    const timeoutRef = useRef(undefined);
    const shiftToCenter = useCallback((index) => {
        if (index === -1 || index > lineGraph.length - 1) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            if (list)
                list.current?.scrollToIndex({
                    index,
                    viewPosition: 0,
                    animated: true,
                });
        }, 150);
    }, [lineGraph.length]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const list = useRef(null);
    const onPressBar = useCallback((index) => {
        onMonthSelected(index);
        shiftToCenter(index);
    }, [onMonthSelected, shiftToCenter]);
    const renderBar = useCallback(({ index, item }) => (<CategoriesHistogramBar item={item} onPress={() => onPressBar(index)} categoryMerchantSelected={categoryMerchantSelected} barColor={color} isSelected={index === selectedIndex} barContainerHeight={barContainerHeight} baselineTop={baselineTop} baselineHeight={baselineHeight}/>), [
        categoryMerchantSelected,
        color,
        selectedIndex,
        barContainerHeight,
        baselineTop,
        baselineHeight,
        onPressBar,
    ]);
    const isUserScrolling = useRef(false);
    const handleScrollBeginDrag = useCallback(() => {
        isUserScrolling.current = true;
    }, []);
    const handleMomentumScrollEnd = useCallback(() => {
        if (isUserScrolling.current) {
            triggerSelectorPress();
            isUserScrolling.current = false;
        }
    }, []);
    const average = useMemo(() => {
        if (lineGraph.length === 0)
            return null;
        const value = sum / lineGraph.length;
        const signedHeight = value * scale;
        const top = signedHeight > 0
            ? baselineTop - signedHeight
            : baselineTop + baselineHeight - signedHeight;
        return { value, top };
    }, [baselineHeight, baselineTop, lineGraph.length, scale, sum]);
    return (<View style={styles.container}>
      {drawBaseline ? (<View style={[styles.baseline, { top: baselineTop }]}/>) : null}

      {average !== null && (<View style={[styles.averageLabel, { top: average.top }]}>
          <View style={styles.averageLabelText}>
            <Text Text-12 Secondary>
              Avg.
            </Text>
          </View>
          <Amount Numbers-16 value={{ value: Math.abs(average.value), currency }}/>
        </View>)}

      <View style={styles.leftPane}>
        <FlatList ref={list} horizontal inverted showsHorizontalScrollIndicator={false} onScrollToIndexFailed={noop} initialScrollIndex={selectedIndex} renderItem={renderBar} data={barItems} keyExtractor={keyExtractor} snapToAlignment="start" snapToInterval={BAR_OUTER_WIDTH} decelerationRate="fast" onScrollBeginDrag={handleScrollBeginDrag} onMomentumScrollEnd={handleMomentumScrollEnd} getItemLayout={getItemLayout}/>
        {average !== null && <DashedLine top={average.top}/>}
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        height: CARD_HEIGHT,
        flexDirection: 'row-reverse',
    },
    averageLabel: {
        height: rem(36),
        marginTop: -rem(18),
        marginHorizontal: rem(8),
        justifyContent: 'center',
    },
    averageLabelText: {
        position: 'absolute',
        top: -rem(12),
    },
    leftPane: {
        flex: 1,
    },
    baseline: {
        position: 'absolute',
        height: BASELINE_HEIGHT,
        width: '100%',
        backgroundColor: colors.borders.divider,
    },
}));
export default memo(CategoriesHistogram);
